import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Typography,
  Spin,
  Card,
  Descriptions,
  Table,
  Tag,
  Space,
  Button,
  Switch,
  Popconfirm,
  message,
} from "antd";

import {
  privateAdminGetApi,
  privateAdminPostApi,
  privateAdminPutApi,
} from "../../utils/apiHelper";

const { Title, Text } = Typography;

function getAdminRole() {
  try {
    return JSON.parse(localStorage.getItem("adminUser") || "{}")?.role;
  } catch {
    return undefined;
  }
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const canEdit = getAdminRole() === "superadmin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const fetchUser = useCallback(() => {
    setLoading(true);
    privateAdminGetApi(`admin/users/${id}`)
      .then((res) => {
        setData(res.data.result);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const sendLoginOtp = async () => {
    setSendingOtp(true);
    try {
      await privateAdminPostApi(`admin/users/${id}/send-login-otp`, {});
      message.success("Login OTP sent.");
    } catch {
      // toast already shown
    } finally {
      setSendingOtp(false);
    }
  };

  const resetPassword = async () => {
    setResettingPassword(true);
    try {
      await privateAdminPostApi(`admin/users/${id}/reset-password`, {});
      message.success("Password cleared. User must log in via OTP.");
      fetchUser();
    } catch {
      // toast already shown
    } finally {
      setResettingPassword(false);
    }
  };

  const toggleActive = async (nextActive) => {
    setTogglingActive(true);
    try {
      await privateAdminPutApi(`admin/users/${id}/active-status`, {
        active: nextActive,
      });
      message.success(nextActive ? "Account reactivated." : "Account deactivated.");
      fetchUser();
    } catch {
      // toast already shown
    } finally {
      setTogglingActive(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!data) {
    return <Text type="secondary">Failed to load user.</Text>;
  }

  const { user, memberships } = data;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Link to="/admin/organizations">&larr; Back to organizations</Link>
        <Title level={5} style={{ marginTop: 8, marginBottom: 0 }}>
          {user.firstName || user.lastName
            ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
            : user.emailAddress}
        </Title>
        <Text type="secondary">{user.emailAddress}</Text>
      </div>

      <Card size="small" title="Account">
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Email">{user.emailAddress}</Descriptions.Item>
          <Descriptions.Item label="Phone">{user.phoneNumber || "-"}</Descriptions.Item>
          <Descriptions.Item label="Has password set">
            {user.hasPassword ? "Yes" : "No (OTP only)"}
          </Descriptions.Item>
          <Descriptions.Item label="Account status">
            <Space>
              <Tag color={user.activeAccount ? "green" : "red"}>
                {user.activeAccount ? "Active" : "Deactivated"}
              </Tag>
              {canEdit && (
                <Popconfirm
                  title={
                    user.activeAccount
                      ? "Deactivate this account?"
                      : "Reactivate this account?"
                  }
                  description={
                    user.activeAccount
                      ? "The user will no longer be able to log in to any of their organizations."
                      : undefined
                  }
                  onConfirm={() => toggleActive(!user.activeAccount)}
                >
                  <Switch
                    checked={user.activeAccount}
                    loading={togglingActive}
                    checkedChildren="Active"
                    unCheckedChildren="Deactivated"
                  />
                </Popconfirm>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}
          </Descriptions.Item>
        </Descriptions>

        {canEdit && (
          <Space style={{ marginTop: 16 }}>
            <Button loading={sendingOtp} onClick={sendLoginOtp}>
              Send login OTP
            </Button>
            <Popconfirm
              title="Clear this user's password?"
              description="They'll need to log in via OTP and set a new password. This does not set a password on their behalf."
              onConfirm={resetPassword}
            >
              <Button loading={resettingPassword} danger>
                Force password reset
              </Button>
            </Popconfirm>
          </Space>
        )}
      </Card>

      <Card size="small" title={`Organizations (${memberships.length})`}>
        <Table
          rowKey={(r) => r.organizationId}
          size="small"
          pagination={false}
          dataSource={memberships}
          columns={[
            {
              title: "Organization",
              dataIndex: "organizationName",
              key: "organizationName",
              render: (name, r) =>
                r.organizationId ? (
                  <Link to={`/admin/organizations/${r.organizationId}`}>
                    {name || r.organizationId}
                  </Link>
                ) : (
                  name || "-"
                ),
            },
            { title: "Role", dataIndex: "role", key: "role" },
            { title: "Status", dataIndex: "status", key: "status" },
          ]}
        />
      </Card>
    </Space>
  );
}
